import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import * as Yup from "yup";

import { ApiError, getEntryByCompetitorNumber } from "../../../../../api";
import useAsyncMemo from "../../../../../hooks/useAsyncMemo";
import useCompetition from "../../../../../hooks/useCompetition";
import useDebounce from "../../../../../hooks/useDebounce";
import useErrorAlert from "../../../../../hooks/useErrorAlert";
import { ApiCompetitionEntry } from "../../../../../types";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";

const schema = Yup.object().shape({
  competitorNumber: Yup.string().required("Required"),
});

export type CompetitorByNumberData = {
  firstName: string;
  lastName: string;
  competitorNumber: string;
  uuid: string;
};

const getEntryData = async (
  competitionUUID: string,
  competitorNumber: string
) => {
  if (!competitorNumber) {
    return undefined;
  }
  try {
    return await getEntryByCompetitorNumber(competitionUUID, competitorNumber);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    } else {
      return undefined;
    }
  }
};

export default function useCompetitorNumberScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const { competition } = useCompetition(params.competitionUUID);

  const [competitorNumber, setCompetitorNumber] = useState("");
  const competitorNumberDebounced = useDebounce(competitorNumber, 500);
  const [entryData, isFetchingEntryData] = useAsyncMemo(
    () => getEntryData(params.competitionUUID || "", competitorNumberDebounced),
    [competitorNumberDebounced, params.competitionUUID]
  );

  const { setError } = useErrorAlert();

  const onSubmit = async (values: { competitorNumber: string }) => {
    try {
      const data = await getEntryByCompetitorNumber(
        params.competitionUUID || "",
        values.competitorNumber
      );
      if (data) {
        const existingList: CompetitorByNumberData[] = JSON.parse(
          localStorage.getItem(
            `${params.competitionUUID}/competitorNumberEntries`
          ) || "[]"
        );
        localStorage.setItem(
          `${params.competitionUUID}/competitorNumberEntries`,
          JSON.stringify([
            ...existingList.filter(
              (x) => x.competitorNumber !== competitorNumber
            ),
            {
              firstName: data.user.firstName,
              lastName: data.user.lastName,
              competitorNumber,
              uuid: data.user.uuid,
            },
          ])
        );
        navigate(`/${params.competitionUUID}?entry=${data.user.uuid}`);
      }
      // onNext({ competitorNumber, competitorNumberExists: competitorNumberExists_ });
    } catch (error) {
      setError(getGenericErrorInfo(error));
    }
  };

  const initialValues = { competitorNumber: "" };

  return {
    competition,
    entryData,
    isFetchingEntryData,
    onSubmit,
    initialValues,
    setCompetitorNumber,
    schema,
  };
}
